import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { isAuthSwitchedElsewhere, subscribeAuthSwitched } from '../utils/authTabSync';

/**
 * D-279 (QX-ב) — another tab signed in as someone else (or out): this tab is
 * covered by a blocking message until it is refreshed. Every request of the
 * tab is refused meanwhile (api clients), so nothing is sent as the wrong user.
 * The page is NOT reloaded on its own: an unsaved form stays under the message.
 */
export default function AuthSwitchedOverlay() {
  const { t } = useTranslation();
  const switched = useSyncExternalStore(subscribeAuthSwitched, isAuthSwitchedElsewhere, isAuthSwitchedElsewhere);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (switched) buttonRef.current?.focus(); }, [switched]);
  // Focus stays on the refresh button while the message is up (Tab cannot leave it).
  useEffect(() => {
    if (!switched) return undefined;
    const keepFocus = (e: KeyboardEvent) => { if (e.key === 'Tab') { e.preventDefault(); buttonRef.current?.focus(); } };
    document.addEventListener('keydown', keepFocus);
    return () => document.removeEventListener('keydown', keepFocus);
  }, [switched]);
  if (!switched) return null;
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="auth-switched-title"
      aria-describedby="auth-switched-body"
      className="fixed inset-0 z-dev flex items-center justify-center bg-black/50 p-4"
    >
      <div className="card max-w-md w-full p-6 text-center shadow-xl">
        <h2 id="auth-switched-title" className="text-lg font-semibold mb-2">{t('authTabSync.title')}</h2>
        <p id="auth-switched-body" className="mb-4">{t('authTabSync.body')}</p>
        <button ref={buttonRef} type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
          {t('authTabSync.refresh')}
        </button>
      </div>
    </div>
  );
}
